import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { removeUITags } from '@/utils/textFilterUtils';
import {
  MicOff as MicOffIcon,
  PauseCircleOutline as PauseIcon
} from '@mui/icons-material';

interface SpeakerSegment {
  id: string;
  speaker: number;
  text: string;
  timestamp: string;
  confidence: number;
  duration: number;
  isPauseSegment?: boolean;
}

interface RecordingDividerSegment {
  id: string;
  kind: 'recording-divider';
  startTimeUtc: string;
}

interface PauseSegment {
  id: string;
  kind: 'pause-segment';
  timestamp: string;
  duration: number;
}

type TranscriptRow = SpeakerSegment | RecordingDividerSegment | PauseSegment;

interface GroupedSegment {
  id: string;
  speaker: number;
  text: string;
  timestamp: string;
  confidence: number;
  segments: SpeakerSegment[];
}

interface SpeakerInfo {
  displayName: string;
  isAssigned: boolean;
  name: string;
  avatar?: string;
}

interface MinimalTranscriptViewProps {
  speakers: TranscriptRow[];
  isRecording: boolean;
  interimTranscript: string;
  speakerMapping?: any;
  onFindingClick?: (finding: any) => void;
}

// Build render items in order without combining: one item per segment, interleaved with recording dividers and pause segments
const buildRenderItems = (
  speakers: TranscriptRow[]
): (GroupedSegment | RecordingDividerSegment | PauseSegment)[] => {
  const items: (GroupedSegment | RecordingDividerSegment | PauseSegment)[] = [];
  for (const row of speakers) {
    if ((row as any).kind === 'recording-divider') {
      items.push(row as RecordingDividerSegment);
      continue;
    }
    if ((row as any).kind === 'pause-segment') {
      items.push(row as PauseSegment);
      continue;
    }
    const seg = row as SpeakerSegment;
    // Check if this is a pause segment (from backend)
    if (seg.isPauseSegment) {
      // Debug logging removed to prevent console spam
      // console.log('[MinimalTranscriptView] Found pause segment:', seg);
      items.push({
        id: seg.id,
        kind: 'pause-segment',
        timestamp: seg.timestamp,
        duration: seg.duration
      } as PauseSegment);
      continue;
    }
    items.push({
      id: seg.id,
      speaker: seg.speaker,
      text: seg.text,
      timestamp: seg.timestamp,
      confidence: seg.confidence,
      segments: [seg]
    });
  }
  return items;
};

// Helper function to get speaker info
const getSpeakerInfo = (speakerIndex: number): SpeakerInfo => {
  return {
    displayName: `Speaker ${speakerIndex}`,
    isAssigned: false,
    name: `Speaker ${speakerIndex}`
  };
};

// Helper function to format timestamp range (uses last segment end time)
const formatTimestampRange = (group: GroupedSegment): string => {
  const groupStart = new Date(group.timestamp);
  let endMs = groupStart.getTime();
  for (const seg of group.segments) {
    const segStartMs = new Date(seg.timestamp).getTime();
    const segEndMs = segStartMs + Math.max(0, seg.duration || 0) * 1000;
    if (segEndMs > endMs) endMs = segEndMs;
  }

  const format = (d: Date) =>
    d.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });

  const startStr = format(groupStart);
  const endStr = format(new Date(endMs));
  return `${startStr} - ${endStr}`;
};

// Helper to format duration in seconds to readable string
const formatDuration = (durationSeconds: number): string => {
  const minutes = Math.floor(durationSeconds / 60);
  const seconds = Math.floor(durationSeconds % 60);
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
};

export const MinimalTranscriptView: React.FC<MinimalTranscriptViewProps> = ({
  speakers,
  isRecording,
  interimTranscript
}) => {
  const theme = useTheme();
  // Interleave dividers and groups in original order
  const renderItems = buildRenderItems(speakers);

  return (
    <>
      {speakers.length === 0 && !isRecording && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px'
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', fontSize: '14px', maxWidth: '300px' }}
          >
            No transcript recorded yet
          </Typography>
        </Box>
      )}

      <Box sx={{ background: theme.palette.background.paper }}>
        {renderItems.map((item) => {
          if ((item as any).kind === 'pause-segment') {
            const pauseSegment = item as PauseSegment;
            const timeStr = new Date(pauseSegment.timestamp).toLocaleTimeString(
              'en-US',
              {
                hour12: false,
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              }
            );
            return (
              <Box
                key={pauseSegment.id}
                sx={{
                  px: 3,
                  py: 1.5,
                  position: 'relative',
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(245, 158, 11, 0.1)'
                      : '#fef3c7',
                  borderLeft: `3px solid ${
                    theme.palette.mode === 'dark' ? '#f59e0b' : '#d97706'
                  }`
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5
                  }}
                >
                  <PauseIcon
                    sx={{
                      fontSize: '18px',
                      color:
                        theme.palette.mode === 'dark' ? '#f59e0b' : '#d97706'
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color:
                        theme.palette.mode === 'dark' ? '#fcd34d' : '#92400e',
                      fontWeight: 500
                    }}
                  >
                    Recording paused
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: theme.palette.text.secondary,
                      ml: 'auto'
                    }}
                  >
                    {timeStr}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color:
                        theme.palette.mode === 'dark' ? '#fbbf24' : '#b45309',
                      fontWeight: 500
                    }}
                  >
                    {formatDuration(pauseSegment.duration)}
                  </Typography>
                </Box>
              </Box>
            );
          }
          if ((item as any).kind === 'recording-divider') {
            const divider = item as RecordingDividerSegment;
            const timeStr = new Date(divider.startTimeUtc).toLocaleTimeString(
              'en-US',
              {
                hour12: false,
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              }
            );
            return (
              <Box
                key={divider.id}
                sx={{
                  px: 3,
                  py: 1.5,
                  position: 'relative',
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : '#f3f4f6'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5
                  }}
                >
                  <Box
                    sx={{
                      height: 1,
                      backgroundColor: theme.palette.divider,
                      flexGrow: 1
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: theme.palette.text.secondary,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    New recording started at {timeStr}
                  </Typography>
                  <Box
                    sx={{
                      height: 1,
                      backgroundColor: theme.palette.divider,
                      flexGrow: 1
                    }}
                  />
                </Box>
              </Box>
            );
          }
          const groupedSegment = item as GroupedSegment;
          const speakerInfo = getSpeakerInfo(groupedSegment.speaker);
          const isGrouped = groupedSegment.segments.length > 1;

          // Filter out UI tags from the text for transcript display
          const filteredText = removeUITags(groupedSegment.text || '');

          // Skip rendering if the filtered text is empty (segment only contained UI tags)
          if (!filteredText.trim()) {
            return null;
          }

          return (
            <Box
              key={groupedSegment.id}
              sx={{
                px: 3,
                py: 2,
                position: 'relative',
                transition: 'background 0.15s ease',
                borderBottom: '1px solid transparent',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover
                }
              }}
            >
              {/* Speaker Line */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 1.5,
                  mb: 1
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: 'white',
                    flexShrink: 0,
                    backgroundColor:
                      groupedSegment.speaker === 0
                        ? theme.palette.mode === 'dark'
                          ? '#3b82f6'
                          : '#171717'
                        : theme.palette.mode === 'dark'
                        ? '#6b7280'
                        : '#a3a3a3'
                  }}
                >
                  {groupedSegment.speaker}
                </Box>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: theme.palette.text.primary
                  }}
                >
                  {speakerInfo.displayName}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: theme.palette.text.secondary,
                    ml: 'auto'
                  }}
                >
                  {formatTimestampRange(groupedSegment)}
                </Typography>
                {groupedSegment.confidence > 0 && (
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color:
                        groupedSegment.confidence > 0.8
                          ? theme.palette.text.primary
                          : theme.palette.text.secondary,
                      ml: 1
                    }}
                  >
                    {Math.round(groupedSegment.confidence * 100)}%
                  </Typography>
                )}
                {isGrouped && (
                  <Typography
                    sx={{
                      fontSize: '11px',
                      color: '#3b82f6',
                      ml: 1.5,
                      cursor: 'pointer',
                      textDecoration: 'none',
                      transition: 'opacity 0.15s ease',
                      '&:hover': {
                        opacity: 0.7
                      }
                    }}
                  >
                    {groupedSegment.segments.length} parts
                  </Typography>
                )}
              </Box>

              {/* Transcript Text */}
              <Box
                sx={{
                  ml: 4,
                  fontSize: '14px',
                  lineHeight: 1.6,
                  color:
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.7)'
                      : '#525252',
                  textAlign: 'left'
                }}
              >
                <Typography
                  component="div"
                  sx={{
                    fontSize: 'inherit',
                    lineHeight: 'inherit',
                    color: 'inherit',
                    whiteSpace: 'pre-wrap',
                    textAlign: 'left'
                  }}
                >
                  {filteredText}
                </Typography>
              </Box>
            </Box>
          );
        })}

        {/* Interim Transcript */}
        {interimTranscript && (
          <Box
            sx={{
              px: 3,
              py: 2,
              position: 'relative',
              backgroundColor: theme.palette.action.hover,
              opacity: 0.7
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 1.5,
                mb: 1
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'white',
                  flexShrink: 0,
                  backgroundColor:
                    theme.palette.mode === 'dark' ? '#6b7280' : '#a3a3a3'
                }}
              >
                ?
              </Box>
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 500,
                  color: theme.palette.text.primary
                }}
              >
                Processing
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  color: theme.palette.text.secondary,
                  ml: 'auto'
                }}
              >
                ongoing
              </Typography>
            </Box>

            <Box
              sx={{
                ml: 4,
                fontSize: '14px',
                lineHeight: 1.6,
                color: theme.palette.text.secondary,
                fontStyle: 'italic',
                textAlign: 'left'
              }}
            >
              {interimTranscript}
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};
